import { ButtonLoader } from "./intelligent-bookseller-button-loader.js";
import * as StateManager from "./intelligent-bookseller-state.js";
import * as QuizManager from "./intelligent-bookseller-quiz.js";
import * as SuggestionsManager from "./intelligent-bookseller-suggestions.js";

class IntelligentBookseller extends HTMLElement {
  // ============================================
  // Component lifecycle
  // ============================================

  constructor() {
    super();
  }

  connectedCallback() {
    // Global

    this.apiUrl = this.getAttribute('data-api-url');
    if (!this.apiUrl) {
      console.error('API URL is required');
      return;
    }

    this.state = {
      quizLoading: false,
      quizSubmitted: false,
      quizAdvancedQuestionsShown: false,
    };

    this.quizPayload = {
      "cartItems": [
        // {
        //   "id": "9782266282901",
        //   "title": "A la lumière du petit matin",
        //   "author": "Martin-Lugand, Agnès",
        //   "price": 1.99,
        //   "imageUrl": "https://images.centprod.com/...",
        //   "uri": "https://shop.labourseauxlivres.fr/products/9782266282901",
        //   "binding": "Broché",
        //   "pageCount": "384",
        //   "genre": "Romans francophones",
        //   "quantity": 2,
        //   "addedAt": "2025-11-06T10:53:00.914Z",
        //   "addedFrom": "magic_cart"
        // },
        // ... 3 more items
      ],
      "cartTotal": 0,
      "gap": 0,
      "freeShippingThreshold": 0,
      "questionnaire": {
        // "genre": ["Polar"],
        // "purpose": "Pour moi",
        // "interests": "",
        // "abandonedBook": "",
        // "similarBookTitle": "",
        // "similarBookAuthor": "",
        // "similarBookSeries": "",
        // "similarBook": {
        //   "hasReference": false,
        //   "title": "",
        //   "author": "",
        //   "series": ""
        // }
      },
      "searchResults": [],
      "userEmail": "anonymous",
      "visitorId": "",
      "user_pseudo_id": "",
      "timestamp": ""
    };

    // Quiz
    this.quiz = this.querySelector('form[data-intelligent-bookseller-quiz]');
    this.quizQuestions = this.querySelectorAll('[data-question]');
    this.quizRequiredQuestions = this.querySelectorAll('[data-question-required]');
    this.conditionalQuestions = this.querySelectorAll('[data-parent-question-handle][data-conditional-answer]');
    this.quizAdvancedQuestions = this.quiz?.querySelectorAll('[data-intelligent-bookseller-advanced-question]') || [];
    this.quizToggleAdvancedQuestionsBtn = this.querySelector('[data-quiz-toggle-advanced-questions]');
    this.quizToggleAdvancedQuestionsTexts = this.querySelectorAll('[data-quiz-toggle-advanced-questions-text]');
    this.quizSubmitBtn = this.querySelector('[data-quiz-submit]');
    this.quizSubmitText = this.querySelector('[data-quiz-submit-text]');
    this.quizSubmitLoader = this.querySelector('[data-quiz-submit-loader]');

    // Suggestions
    this.suggestions = this.querySelector('[data-intelligent-bookseller-suggestions]');
    this.suggestionsEditQuizBtn = this.querySelector('[data-suggestions-edit-quiz]');
    this.suggestionsEmpty = this.querySelector('[data-suggestions-empty]');
    this.suggestionsDescription = this.querySelector('[data-suggestions-description]');
    this.suggestionsDescriptionText = this.querySelector('[data-suggestions-description-text]');
    this.suggestionsItems = this.querySelector('[data-suggestions-items]');
    this.suggestionsRefreshBtn = this.querySelector('[data-suggestions-refresh-button]');
    this.suggestionsRefreshBtnText = this.querySelector('[data-suggestions-refresh-button-text]');
    this.suggestionsRefreshBtnLoader = this.querySelector('[data-suggestions-refresh-button-loader]');
    this.suggestionsRefreshErrorMsg = this.querySelector('[data-refresh-suggestions-error-msg]');
    this.suggestionsAddAllBtn = this.querySelector('[data-suggestions-add-all]');
    this.suggestionsAddAllBtnText = this.querySelector('[data-suggestions-add-all-text]');
    this.suggestionsAddAllBtnLoader = this.querySelector('[data-suggestions-add-all-loader]');
    this.suggestionsAddAllErrorMsg = this.querySelector('[data-suggestions-add-all-error-msg]');
    this.suggestionsAddAllSuccessMsg = this.querySelector('[data-suggestions-add-all-success-msg]');

    // Debug
    this.debugMode = false; // Set to true to enable debug mode
    
    // Init
    if (this.quiz && this.suggestions) {
      // Avoid multiple initialization
      if (!this._initialized) {
        this._initialized = true;
        this.init();
      }
    }
  }

  disconnectedCallback() {
    // Quiz toggle advanced questions
    if (this.quizToggleAdvancedQuestionsBtn && this._handleToggleAdvancedQuestions) {
      this.quizToggleAdvancedQuestionsBtn.removeEventListener('click', this._handleToggleAdvancedQuestions);
      this._handleToggleAdvancedQuestions = null;
    }
    
    // Conditional questions
    if (this._conditionalQuestionHandlers && Array.isArray(this._conditionalQuestionHandlers)) {
      this._conditionalQuestionHandlers.forEach(({ element, handler }) => {
        if (element && handler) {
          element.removeEventListener('change', handler);
        }
      });
      this._conditionalQuestionHandlers = null;
    }
    
    // Quiz submit
    if (this.quiz && this._handleQuizSubmitHandler) {
      this.quiz.removeEventListener('submit', this._handleQuizSubmitHandler);
      this._handleQuizSubmitHandler = null;
    }
    
    // Suggestions edit quiz button
    if (this.suggestionsEditQuizBtn && this._handleEditQuizHandler) {
      this.suggestionsEditQuizBtn.removeEventListener('click', this._handleEditQuizHandler);
      this._handleEditQuizHandler = null;
    }
    
    // Suggestions refresh button
    if (this.suggestionsRefreshBtn && this._handleRefreshHandler) {
      this.suggestionsRefreshBtn.removeEventListener('click', this._handleRefreshHandler);
      this._handleRefreshHandler = null;
    }
    
    // Suggestions add all button
    if (this.suggestionsAddAllBtn && this._handleAddAllHandler) {
      this.suggestionsAddAllBtn.removeEventListener('click', this._handleAddAllHandler);
      this._handleAddAllHandler = null;
    }
    
    // Reset the initialization flag
    this._initialized = false;
  }

  // ============================================
  // Initialization
  // ============================================

  async init() {
    // Debug: Initialize state with mock data (for debug only)
    if (this.debugMode && import.meta.env.DEV) {
      this.initDebugState();
    }

    // Load saved state
    const savedState = StateManager.loadState();
    if (savedState) {
      // Restore questionnaire data
      if (savedState.questionnaire) {
        QuizManager.restoreQuizData(savedState.questionnaire, this.quizQuestions);
      }
      
      // Restore API response if present
      if (savedState.apiResponse) {
        this.state.apiResponse = savedState.apiResponse;
      }
      
      // Restore quiz submitted state
      if (savedState.quizSubmitted) {
        this.state.quizSubmitted = savedState.quizSubmitted;
      }
    }

    // Init quiz
    this.initQuiz();

    // Init suggestions
    this.initSuggestions();

    // Restore suggestions content if needed
    await this.restoreSuggestionsIfNeeded();

    // Init content (will show quiz or suggestions based on state)
    this.setContent();
  }

  initQuiz() {
    // Init quiz advanced questions
    this._handleToggleAdvancedQuestions = QuizManager.initQuizAdvancedQuestions(
      this.quizAdvancedQuestions,
      this.quizToggleAdvancedQuestionsBtn,
      this.quizToggleAdvancedQuestionsTexts,
      this.state
    );

    // Init conditional questions
    this._conditionalQuestionHandlers = QuizManager.initConditionalQuestions(
      this.conditionalQuestions,
      this
    );

    // Init quiz submit
    this._handleQuizSubmitHandler = QuizManager.handleQuizSubmit(
      this.quiz,
      {
        hideAllErrorMessages: () => QuizManager.hideAllErrorMessages(this.quizRequiredQuestions, this),
        toggleLoader: () => {
          this.state.quizLoading = ButtonLoader.toggleLoading(
            this.quizSubmitBtn,
            this.quizSubmitText,
            this.quizSubmitLoader
          );
        },
        quizValidate: () => QuizManager.quizValidate(this.quizRequiredQuestions, this),
        buildPayload: () => this.buildPayload(),
        fetchMagicCartSuggestions: (payload) => this.fetchMagicCartSuggestions(payload),
        setSuggestionsContent: (apiResponse) => this.setSuggestionsContent(apiResponse),
        setContent: () => this.setContent(),
        saveState: () => this.saveState(),
        hasAdvancedQuestionsAnswered: () => QuizManager.hasAdvancedQuestionsAnswered(this.quizAdvancedQuestions)
      },
      this.state,
      this
    );
  }

  initSuggestions() {
    // Edit quiz button
    this._handleEditQuizHandler = SuggestionsManager.initSuggestionsEditQuizButton(
      this.suggestionsEditQuizBtn,
      this.state,
      {
        restoreQuizData: (questionnaireData) => QuizManager.restoreQuizData(questionnaireData, this.quizQuestions),
        setContent: () => this.setContent()
      }
    );

    // Refresh suggestions button
    this._handleRefreshHandler = SuggestionsManager.initSuggestionsRefreshButton(
      this.suggestionsRefreshBtn,
      {
        suggestionsRefreshBtnText: this.suggestionsRefreshBtnText,
        suggestionsRefreshBtnLoader: this.suggestionsRefreshBtnLoader,
        suggestionsRefreshErrorMsg: this.suggestionsRefreshErrorMsg
      },
      this.state,
      {
        refreshSuggestions: () => SuggestionsManager.refreshSuggestions(
          this.state,
          { suggestionsItems: this.suggestionsItems },
          {
            buildPayload: () => this.buildPayload(),
            fetchMagicCartSuggestions: (payload) => this.fetchMagicCartSuggestions(payload),
            setSuggestionsContent: (apiResponse) => this.setSuggestionsContent(apiResponse),
            saveState: () => this.saveState()
          }
        )
      }
    );

    // Add all suggestions to cart button
    this._handleAddAllHandler = SuggestionsManager.initSuggestionsAddAllButton(
      this.suggestionsAddAllBtn,
      {
        suggestionsItems: this.suggestionsItems,
        suggestionsAddAllBtnText: this.suggestionsAddAllBtnText,
        suggestionsAddAllBtnLoader: this.suggestionsAddAllBtnLoader,
        suggestionsAddAllErrorMsg: this.suggestionsAddAllErrorMsg,
        suggestionsAddAllSuccessMsg: this.suggestionsAddAllSuccessMsg
      }
    );
  }

  async restoreSuggestionsIfNeeded() {
    // If quiz was submitted and we have a valid API response, load suggestions content
    if (this.state.quizSubmitted === true && this.state.apiResponse) {
      try {
        const success = await this.setSuggestionsContent(this.state.apiResponse);
        if (!success) {
          // If setSuggestionsContent failed, reset to quiz state
          this.state.quizSubmitted = false;
        }
      } catch (error) {
        console.error('Error loading suggestions content on init:', error);
        // Reset to quiz state on error
        this.state.quizSubmitted = false;
      }
    } else if (this.state.quizSubmitted === true && !this.state.apiResponse) {
      // If quizSubmitted is true but no valid API response, reset to quiz state
      console.warn('Quiz submitted but no API response found, resetting to quiz state');
      this.state.quizSubmitted = false;
    }
  }

  // ============================================
  // State management (sessionStorage)
  // ============================================

  saveState() {
    const questionnaire = QuizManager.collectQuizData(this.quizQuestions);
    StateManager.saveState(questionnaire, this.state.apiResponse, this.state.quizSubmitted);
  }

  loadState() {
    return StateManager.loadState();
  }

  clearState() {
    StateManager.clearState();
  }

  // ============================================
  // Debug function (for debug only)
  // ============================================

  initDebugState() {
    try {
      // Create mock products with provided IDs
      const productIds = ["9782253117186", "9782253070566", "9782253934103", "9782070375165"];
      
      const mockProducts = productIds.map((id, index) => ({
        id: id,
        title: `Livre Manga ${index + 1}`,
        author: `Auteur ${index + 1}`,
        price: 5.99 + (index * 0.50), // Varied prices between 5.99 and 7.49
        imageUrl: `https://images.centprod.com/image/${id}.jpg`,
        uri: `https://shop.labourseauxlivres.fr/products/${id}`,
        binding: "Broché",
        pageCount: "200",
        genre: "Manga",
        ai_summary: `Un excellent manga de science-fiction qui correspond à vos goûts.`,
        score: 0.95 - (index * 0.02), // Decreasing score from 0.95 to 0.89
        availability: "IN_STOCK",
        categories: ["Book", "Littérature > Manga"],
        language: "Français"
      }));

      // Create complete mock API response
      const mockApiResponse = {
        status: "success",
        magic_cart_suggestions: {
          suggestions: mockProducts,
          allSuggestions: mockProducts, // Use the same products for allSuggestions
          summary: "Voici une sélection de mangas de science-fiction qui correspondent à vos préférences et qui vous permettront d'atteindre les frais de port gratuits.",
          combinationMetadata: {
            suggestionsCount: 4,
            suggestionsTotal: 26.97,
            gap: 20.00,
            gapRemaining: 0,
            freeShippingThreshold: 20,
            estimatedNewTotal: 26.97,
            reachesFreeShipping: true,
            algorithm: "combinatorial",
            combinationsFound: true
          }
        },
        search_summary: {
          original_query: "Magic Cart",
          optimized_query: "manga science-fiction",
          total_results: 4,
          personalization_level: "none",
          personalization_strategy: "Magic Cart gap-filling with combinatorial optimization",
          user_type: "anonymous"
        },
        questionnaire: {
          genre: ["Manga"],
          purpose: "Pour moi",
          interests: "Science"
        },
        cart_info: {
          gap: 20.00,
          cart_total: 0.00,
          cart_items_count: 0,
          free_shipping_threshold: 20
        },
        user_preferences: null,
        metadata: {
          has_user_data: false,
          data_source: "DEBUG",
          is_magic_cart: true,
          workflow_type: "magic_cart_v1"
        }
      };

      // Prepare questionnaire data
      const questionnaire = {
        genre: ["Manga"],
        purpose: "Pour moi",
        interests: "Science"
      };

      // Save to sessionStorage
      StateManager.saveState(questionnaire, mockApiResponse, true);
      
      // Then set state directly to ensure it's available immediately
      this.state.apiResponse = mockApiResponse;
      this.state.quizSubmitted = true;
      
      console.log('Debug state initialized and saved to sessionStorage');
      console.log('Current state:', this.state);
    } catch (error) {
      console.error('Error initializing debug state:', error);
    }
  }

  // ============================================
  // Display management
  // ============================================

  setContent() {
    if (this.state.quizSubmitted === false) {
      this.removeFocusFromContainer(this.suggestions);
      this.suggestions.setAttribute('aria-hidden', true);
      this.quiz.removeAttribute('aria-hidden');
    } else {
      this.removeFocusFromContainer(this.quiz);
      this.quiz.setAttribute('aria-hidden', true);
      this.suggestions.removeAttribute('aria-hidden');
    }
  }

  removeFocusFromContainer(container) {
    if (!container) return;
    
    // Selector for all focusable elements
    const focusableSelectors = [
      'button',
      'input',
      'select',
      'textarea',
      'a[href]'
    ].join(', ');
    
    const focusableElements = container.querySelectorAll(focusableSelectors);
    focusableElements.forEach((element) => {
      if (element === document.activeElement) {
        element.blur();
      }
    });
  }

  // ============================================
  // Cart and user data
  // ============================================

  async fetchCartDataFromSection() {
    try {
      const sectionId = 'intelligent-bookseller-cart-data';
      const url = `${window.Shopify.routes.root}?sections=${sectionId}`;
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch section: ${response.status}`);
      }

      const json = await response.json();
      const sectionHtml = json[sectionId];
      
      if (!sectionHtml) {
        throw new Error('Section not found in response');
      }

      // Parse HTML to extract JSON script content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = sectionHtml;
      const scriptElement = tempDiv.querySelector('script[type="application/json"]');
      
      if (!scriptElement) {
        throw new Error('JSON script not found in section');
      }

      const cartData = JSON.parse(scriptElement.textContent);
      return cartData;
    } catch (error) {
      console.error('Error fetching cart data from section:', error);
      throw error;
    }
  }

  async getCartData() {
    try {
      const cartData = await this.fetchCartDataFromSection();
      const { 
        cartItems = [], 
        cartTotal = 0,
        freeShippingThreshold = 20.00,
        gap = 20.00
      } = cartData;

      return {
        cartItems,
        cartTotal,
        gap,
        freeShippingThreshold
      };
    } catch (error) {
      console.error('Error fetching cart data:', error);
      return {
        cartItems: [],
        cartTotal: 0,
        gap: 20.00,
        freeShippingThreshold: 20.00
      };
    }
  }

  getUserIdentifiers() {
    // Get visitorId from localStorage
    const visitorId = localStorage.getItem('visitorId') || '';

    // Get user_pseudo_id from _ga cookie (format: GA1.2.clientId.timestamp)
    let user_pseudo_id = '';
    const gaCookie = document.cookie.split(';').find(cookie => cookie.trim().startsWith('_ga='));
    if (gaCookie) {
      const gaValue = gaCookie.split('=')[1];
      // _ga format is GA1.2.clientId.timestamp
      const parts = gaValue.split('.');
      if (parts.length >= 4) {
        user_pseudo_id = `${parts[0]}.${parts[1]}.${parts[2]}.${parts[3]}`;
      }
    }

    // Get userEmail
    const userEmail = this.dataset.userEmail || 'anonymous';

    return {
      visitorId,
      user_pseudo_id,
      userEmail
    };
  }

  // ============================================
  // API and payload
  // ============================================

  async buildPayload() {
    const questionnaire = QuizManager.collectQuizData(this.quizQuestions);
    const { cartItems, cartTotal, gap, freeShippingThreshold } = await this.getCartData();
    const { visitorId, user_pseudo_id, userEmail } = this.getUserIdentifiers();

    const payload = {
      cartItems,
      cartTotal,
      gap,
      freeShippingThreshold,
      questionnaire,
      searchResults: [],
      userEmail,
      visitorId,
      user_pseudo_id,
      timestamp: new Date().toISOString()
    };

    return payload;
  }

  async fetchMagicCartSuggestions(payload) {
    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching magic cart suggestions:', error);
      throw error;
    }
  }

  // ============================================
  // Suggestions - Display
  // ============================================

  async setSuggestionsContent(apiResponse) {
    return SuggestionsManager.setSuggestionsContent(
      apiResponse,
      {
        suggestionsEmpty: this.suggestionsEmpty,
        suggestionsDescription: this.suggestionsDescription,
        suggestionsDescriptionText: this.suggestionsDescriptionText,
        suggestionsItems: this.suggestionsItems,
        suggestionsAddAllBtn: this.suggestionsAddAllBtn
      },
      (products, selectedProducts) => SuggestionsManager.populateSuggestionsItemsFromSection(
        products,
        selectedProducts,
        this.suggestionsItems
      ),
      (elements) => SuggestionsManager.showEmptySuggestions(elements)
    );
  }

  showEmptySuggestions() {
    SuggestionsManager.showEmptySuggestions({
      suggestionsEmpty: this.suggestionsEmpty,
      suggestionsDescription: this.suggestionsDescription,
      suggestionsDescriptionText: this.suggestionsDescriptionText,
      suggestionsItems: this.suggestionsItems,
      suggestionsAddAllBtn: this.suggestionsAddAllBtn
    });
  }

  populateSuggestionsItemsFromSection(products, selectedProducts) {
    return SuggestionsManager.populateSuggestionsItemsFromSection(
      products,
      selectedProducts,
      this.suggestionsItems
    );
  }

  // ============================================
  // Suggestions - Actions
  // ============================================

  removeItem(productHandle) {
    SuggestionsManager.removeItem(
      productHandle,
      this.state,
      { suggestionsItems: this.suggestionsItems },
      (elements) => SuggestionsManager.showEmptySuggestions({
        suggestionsEmpty: this.suggestionsEmpty,
        suggestionsDescription: this.suggestionsDescription,
        suggestionsDescriptionText: this.suggestionsDescriptionText,
        suggestionsItems: this.suggestionsItems,
        suggestionsAddAllBtn: this.suggestionsAddAllBtn
      }),
      () => this.saveState()
    );
  }

  switchItem(productHandle) {
    SuggestionsManager.switchItem(
      productHandle,
      this.state,
      { suggestionsItems: this.suggestionsItems },
      () => this.saveState()
    );
  }

  async refreshSuggestions() {
    return SuggestionsManager.refreshSuggestions(
      this.state,
      { suggestionsItems: this.suggestionsItems },
      {
        buildPayload: () => this.buildPayload(),
        fetchMagicCartSuggestions: (payload) => this.fetchMagicCartSuggestions(payload),
        setSuggestionsContent: (apiResponse) => this.setSuggestionsContent(apiResponse),
        saveState: () => this.saveState()
      }
    );
  }
}

if (!customElements.get('intelligent-bookseller')) {
  customElements.define('intelligent-bookseller', IntelligentBookseller);
}
